import React, { useState, useEffect } from "react";
import APIClient from "web_modules/APIClient";
import {
  CitizenshipStatuses,
  Declaration as DDeclaration,
  LoanApplicationBorrowers,
} from "@eave/shared/types/api";

import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import { Button } from "carbon-components-react";
import { prepareForApp } from "@eave/shared/utils/prepareForApp";
import { prepareForAPI } from "@eave/shared/utils/prepareForAPI";
import BorrowerDeclaration from "underwriter_dashboard/hurdle_editors/Declarations/BorrowerDeclaration";
import { loadApp } from "underwriter_dashboard/actions";
import { connect } from "react-redux";
import { cloneDeep } from "lodash";
import Spinner from "web_modules/shared/Spinner";
import { LoadingState } from "underwriter_dashboard/shared/types";
import { borrowersAscending } from "@eave/shared/utils/borrowers";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");
const declarationsStyles = require("underwriter_dashboard/hurdle_editors/Declarations/declarations.module.scss");

interface HurdleEditorProps extends HurdleProps {
  reloadApp: Function;
}

export interface DeclarationError {
  fieldInError: string;
  errorMessage: string;
}

const stringIsNullOrEmpty = (field: string): boolean => {
  return !field || field.length === 0;
};

const validateDeclaration = (declaration: DDeclaration): DeclarationError[] => {
  const errors: DeclarationError[] = [];
  const {
    citizenship,
    propertyOwnershipInterest,
    loanResultingInForeclosure,
    delinquentOrDefaulted,
    bankruptcy,
  } = declaration;
  if (
    citizenship.status === CitizenshipStatuses.other &&
    stringIsNullOrEmpty(citizenship.explanation)
  ) {
    errors.push({
      fieldInError: "citizenship.explanation",
      errorMessage: `An explanation is required when citizenship status is: ${citizenship.status}`,
    });
  } else if (
    citizenship.status === CitizenshipStatuses.nonPermanentResident &&
    citizenship.confirmation == null
  ) {
    errors.push({
      fieldInError: "citizenship.confirmation",
      errorMessage: "Confirmation is required when citizenship status is: Non-Permanent Resident",
    });
  }

  if (propertyOwnershipInterest != null && propertyOwnershipInterest.usage == null) {
    errors.push({
      fieldInError: "propertyOwnershipInterest.usage",
      errorMessage: "Type of property is required",
    });
  }

  if (propertyOwnershipInterest != null && propertyOwnershipInterest.titleOwnership == null) {
    errors.push({
      fieldInError: "propertyOwnershipInterest.titleOwnership",
      errorMessage: "Type of ownership is required",
    });
  }

  if (
    loanResultingInForeclosure != null &&
    stringIsNullOrEmpty(loanResultingInForeclosure.explanation)
  ) {
    errors.push({
      fieldInError: "loanResultingInForeclosure.explanation",
      errorMessage: "An explanation is required for Loan Resulting in a Foreclosure question",
    });
  }

  if (delinquentOrDefaulted != null && stringIsNullOrEmpty(delinquentOrDefaulted.explanation)) {
    errors.push({
      fieldInError: "delinquentOrDefaulted.explanation",
      errorMessage: "An explanation is required for Delinquent or in Default question",
    });
  }

  if (bankruptcy != null && bankruptcy.bankruptcyType.length === 0) {
    errors.push({
      fieldInError: "bankruptcy.bankruptcyType",
      errorMessage: "The type(s) of Bankruptcy must be recorded for the borrower",
    });
  }

  return errors;
};

const initialDeclarationErrors = (borrowers: LoanApplicationBorrowers) => {
  return Object.values(borrowers).reduce((acc, borrower) => {
    return { ...acc, [borrower.borrower_entity]: [] };
  }, {});
};

const initialDeclarationsEdited = (borrowers: LoanApplicationBorrowers) => {
  return Object.values(borrowers).reduce((acc, borrower) => {
    return { ...acc, [borrower.borrower_entity]: false };
  }, {});
};

const Declarations: React.FC<HurdleEditorProps> = ({ app, hurdle, onHide, reloadApp }) => {
  const [loading, setLoading] = useState(LoadingState.INACTIVE);
  const [declarations, setDeclarations] = useState({});
  const [prevDeclarations, setPrevDeclarations] = useState({});
  const [declarationsEdited, setDeclarationsEdited] = useState(
    initialDeclarationsEdited(app.borrowers),
  );
  const [declarationErrors, setDeclarationErrors] = useState(
    initialDeclarationErrors(app.borrowers),
  );

  useEffect(() => {
    fetchDeclarations();
  }, []);

  const fetchDeclarations = () => {
    setLoading(LoadingState.ACTIVE);
    APIClient.get(`/uw/${app.id}/declarations`, {})
      .then((resp: any) => {
        mapToBorrowerEntityAndSetDeclarations(prepareForApp(resp.declarations));
      })
      .catch(() => {
        alert(
          "there was an error getting the declarations, please contact engineering or reload the page and try again",
        );
      })
      .finally(() => {
        setLoading(LoadingState.INACTIVE);
      });
  };

  /**
   * mapToBorrowerEntityAndSetDeclarations takes in the declarations and formats it in key value pairs
   * of [borrower_entity]: declaration
   * @param declarations
   */
  const mapToBorrowerEntityAndSetDeclarations = (declarations: DDeclaration[]) => {
    const declarationsMap = Object.values(declarations).reduce((acc, declaration) => {
      return { ...acc, [declaration.borrowerEntity]: declaration };
    }, {});
    setPrevDeclarations(declarationsMap);
    setDeclarations(declarationsMap);
  };

  const setDeclarationValue = (
    borrowerEntity: number,
    key: string,
    value: any,
    subkey?: string,
  ) => {
    setDeclarationsEdited(prevState => ({
      ...prevState,
      [borrowerEntity]: true,
    }));

    const declaration = declarations[borrowerEntity];
    if (subkey != null) {
      if (declaration[key] == null) {
        declaration[key] = {};
      }
      declaration[key][subkey] = value;
    } else {
      declaration[key] = value;
    }

    setDeclarations(prevState => ({
      ...prevState,
      [borrowerEntity]: declaration,
    }));
  };

  const isDeclarationsEdited = (): boolean => {
    for (const key in declarationsEdited) {
      if (declarationsEdited[key]) {
        return true;
      }
    }
    return false;
  };

  /**
   * resetScreen resets all values for a given borrower entity, or for all of them if the
   * borrower entity is null
   * @param borrowerEntity
   */
  const resetScreen = (borrowerEntity: number = null) => {
    if (borrowerEntity == null) {
      clearErrors();
      resetBorrowerDeclarations();
      setDeclarationsEdited(initialDeclarationsEdited(app.borrowers));
    } else {
      clearErrors(borrowerEntity);
      resetBorrowerDeclarations(borrowerEntity);
      setDeclarationsEdited(prevState => ({
        ...prevState,
        [borrowerEntity]: false,
      }));
    }
  };

  /**
   * clearErrors for a given borrower entity, or for all of them if the
   * borrower entity is null
   * @param borrowerEntity
   */
  const clearErrors = (borrowerEntity: number = null) => {
    if (borrowerEntity == null) {
      setDeclarationErrors(initialDeclarationErrors(app.borrowers));
    } else {
      setDeclarationErrors(prevState => ({
        ...prevState,
        [borrowerEntity]: [],
      }));
    }
  };

  /**
   * resetBorrowerDeclarations to original value for a given borrower entity, or for all of them if the
   * borrower entity is null
   * @param borrowerEntity
   */
  const resetBorrowerDeclarations = (borrowerEntity?: number) => {
    if (borrowerEntity == null) {
      setDeclarations(cloneDeep(prevDeclarations));
    } else {
      setDeclarations(prevState => ({
        ...prevState,
        [borrowerEntity]: cloneDeep(prevDeclarations[borrowerEntity]),
      }));
    }
  };

  /**
   * hasDeclarationErrors checks if a declaration has errors and sets them to the error object. If a borrower
   * entity is passed it will check only for that borrowers declaration errors, if not, it will check for all
   * borrowers declarations.
   * @param borrowerEntity
   */
  const hasDeclarationErrors = (borrowerEntity: number | null) => {
    if (borrowerEntity == null) {
      let hasErrors = false;
      Object.values(declarations).map((d: DDeclaration) => {
        const errors = validateDeclaration(d);
        if (errors.length !== 0) {
          declarationErrors[d.borrowerEntity] = errors;
          if (!hasErrors) {
            hasErrors = true;
          }
        }
      });
      if (hasErrors) {
        setDeclarationErrors(prevState => ({
          ...prevState,
          declarationErrors,
        }));
        return true;
      }
      return false;
    }
    const declaration = declarations[borrowerEntity];
    const errors = validateDeclaration(declaration);
    if (errors.length !== 0) {
      setDeclarationErrors(prevState => ({
        ...prevState,
        [borrowerEntity]: errors,
      }));
      return true;
    }
    return false;
  };

  const saveDeclarationPromise = (declaration: DDeclaration) => {
    return APIClient.put(`/uw/${app.id}/declarations/${declaration.id}`, {
      declarations: prepareForAPI(declaration),
    });
  };

  /**
   * saveDeclaration attempts to update the declaration for the given borrrower entity. If no entity
   * is given it will try to update all declarations that have been edited.
   * @param borrowerEntity
   */
  const saveDeclaration = async (borrowerEntity: number = null) => {
    if (hasDeclarationErrors(borrowerEntity)) {
      alert("Invalid declaration: Please check the error messages on the fields.");
      return;
    }

    const promises = [];
    if (borrowerEntity !== null) {
      promises.push(saveDeclarationPromise(declarations[borrowerEntity]));
    } else {
      Object.values(declarations).forEach((d: DDeclaration) => {
        if (declarationsEdited[d.borrowerEntity]) {
          promises.push(saveDeclarationPromise(d));
        }
      });
    }

    setLoading(LoadingState.ACTIVE);
    await Promise.all(promises)
      .then(() => {
        reloadApp(app.id);
      })
      .catch(() => {
        alert(
          "there was an error saving the declaration, please contact engineering or reload the page and try again",
        );
      })
      .finally(() => {
        setLoading(LoadingState.INACTIVE);
      });
  };

  if (loading === LoadingState.ACTIVE) {
    return <Spinner active={true} />;
  }

  return (
    <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
      {borrowersAscending(app.borrowers).map(b => {
        return (
          <BorrowerDeclaration
            key={b.borrower_entity}
            borrowerEntity={b.borrower_entity}
            borrowerName={`${b.first_name} ${b.last_name}`}
            declaration={declarations[b.borrower_entity]}
            setDeclarationValue={setDeclarationValue}
            resetScreen={resetScreen}
            save={saveDeclaration}
            isEditing={true}
            validationErrors={declarationErrors[b.borrower_entity]}
          />
        );
      })}
      <footer className={styles.footer}>
        {isDeclarationsEdited() ? (
          <div className={declarationsStyles.buttons}>
            <Button kind="secondary" tabIndex={0} onClick={() => resetScreen()}>
              Cancel
            </Button>
            <Button kind="primary" type="submit" tabIndex={0} onClick={() => saveDeclaration()}>
              Save
            </Button>
          </div>
        ) : (
          <p>No changes to save.</p>
        )}
      </footer>
    </HurdleView>
  );
};

const mapDispatchToProps = dispatch => ({
  reloadApp: appID => {
    loadApp(dispatch, appID);
  },
});

export default connect<null, any>(null, mapDispatchToProps)(Declarations);
